import { DB_SCHEMA_TABLE, DbSchemaTable } from './db-types';
import { currentUserId } from '../current-user-id';

type PrimaryKey = number | string;

export abstract class AppIndexedDb<T = any> {

    /**
     * unique table name - currently expects same name as is in gql query
     * f.e.: getFolders has a query: "folder { filter }" -> tableName = 'folder'
     */
    public abstract tableName: string;

    protected db!: IDBDatabase;

    public abstract initializeStore(db: IDBDatabase): void;

    public abstract setDatabase(db: IDBDatabase): void;

    public async add(object: T, store?: IDBObjectStore): Promise<PrimaryKey> {
        store ??= this.getStore('readwrite');
        return this.promise(store.add(object));
    }

    public async put(object: T, store?: IDBObjectStore): Promise<PrimaryKey> {
        store ??= this.getStore('readwrite');
        return this.promise(store.put(object));
    }

    public async delete(key: PrimaryKey, store?: IDBObjectStore): Promise<void> {
        store ??= this.getStore('readwrite');
        return this.promise(store.delete(key));
    }

    public async get(key: PrimaryKey, store?: IDBObjectStore): Promise<T> {
        store ??= this.getStore();
        return this.promise(store.get(key));
    }

    public async getMany(keys: PrimaryKey[], store?: IDBObjectStore): Promise<T[]> {
        store ??= this.getStore();
        return Promise.all(keys.map((key) => this.get(key, store)));
    }

    public async getAll(store?: IDBObjectStore): Promise<T[]> {
        store ??= this.getStore();
        return this.promise(store.getAll());
    }

    /**
     * API has getAllRecords, but it is barely supported in browsers atm, so must
     * call both keys & values and merge them
     */
    public async getAllWithKeys(store?: IDBObjectStore): Promise<T[]> {
        store ??= this.getStore();
        const keys = await this.promise(store.getAllKeys()) as any[];
        const values = await this.promise(store.getAll()) as any[];
        for (let i = 0; i < values.length; i++) {
            values[i].id = keys[i];
        }

        return values as T[];
    }

    /**
     * Returns first entry WITH key
     */
    public async first(store?: IDBObjectStore): Promise<T | null> {
        store ??= this.getStore();
        const key = (await this.promise(store.getAllKeys(null, 1)) as any[])[0];
        if (!key) return null;

        const value = await this.promise(store.get(key)) as any;
        value.id = key;

        return value as T;
    }

    /**
     * Clears both data & schema date marker
     */
    public async clear(): Promise<void> {
        await this.promise(this.getStore('readwrite').clear());
        await this.updateSchema(null);
    }

    protected async getSchema(): Promise<DbSchemaTable> {
        return this.promise(
            this.getStore('readonly', DB_SCHEMA_TABLE)
                .get(this.tableName),
        ).catch(() => ({
            name: this.tableName,
            value: null,
            userId: currentUserId(),
        })) as any;
    }

    protected async updateSchema(date: Date | null): Promise<void> {
        return this.promise(
            this.getStore('readwrite', DB_SCHEMA_TABLE)
                .put({
                    name: this.tableName,
                    value: date?.toISOString(),
                    userId: currentUserId(),
                }),
        );
    }

    /**
     * Clear database on logged user change
     * Returns True if change is triggered
     */
    protected async clearOnUserChange(): Promise<boolean> {
        const userId = currentUserId();
        if (!userId || !this.db) return false;

        let schema = await this.getSchema();
        if (schema && schema?.userId != userId) {
            await this.clear();
            return true;
        }

        return false;
    }

    protected async promise<T>(request: IDBRequest): Promise<T> {
        return new Promise((resolve, reject) => {
            request.onsuccess = function (event: any) {
                resolve(event.target.result);
            };
            request.onerror = function (event: any) {
                console.error(`DbErr: ${event?.target?.error}`);
                reject(event.target.error);
            };
        });
    }

    protected getStore(mode?: IDBTransactionMode, tableName?: string): IDBObjectStore {
        const tx = this.db.transaction(tableName ?? this.tableName, mode);
        return tx.objectStore(tableName ?? this.tableName);
    }

}
